/**
 * What uploading looks like: a calm veil over the canvas while files are
 * dragged in, and a small tray in the corner while they upload. Paper and
 * ink like the rest of the app; signal only while Partnr reads a file.
 */
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDown, Check, File as FileIcon, FileText, Film } from 'lucide-react';
import { cn } from '@/lib/utils';

const SPRING = { type: 'spring' as const, stiffness: 500, damping: 38 };
const EASE_OUT = [0.22, 1, 0.36, 1] as const;

export type UploadKind = 'image' | 'video' | 'pdf' | 'file';

export interface UploadPreview {
  name: string;
  kind: UploadKind;
  /** Object URL of an image, for its thumbnail */
  url: string | null;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// Fanned out behind the front card while the drag hovers
const STACK = [
  { rotate: -9, x: -30, y: 6 },
  { rotate: 7, x: 28, y: 2 },
  { rotate: 0, x: 0, y: -6 },
];

/** Over the canvas while files are dragged in: a paper veil, a small fanned stack, one line. */
export function DropVeil({ count, mode, inset = 0 }: { count: number; mode: 'canvas' | 'wall'; inset?: number }) {
  const still = useReducedMotion();
  const title = count > 1 ? `Drop to add ${plural(count, 'item')}` : mode === 'canvas' ? 'Drop to add to your board' : 'Drop to save to Mindfull';
  return (
    <motion.div
      className="pointer-events-none absolute inset-0 z-[100] p-3"
      style={inset ? { right: inset } : undefined}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: still ? 0 : 0.16, ease: EASE_OUT } }}
      exit={{ opacity: 0, transition: { duration: still ? 0 : 0.12 } }}
      data-testid="drop-veil"
    >
      <div className="flex h-full w-full flex-col items-center justify-center gap-7 rounded-[28px] border border-line bg-paper/80 backdrop-blur-md">
        <div className="relative h-[150px] w-[200px]" aria-hidden>
          {STACK.map((pose, i) => (
            <motion.div
              key={i}
              className="absolute left-1/2 top-1/2 -ml-14 -mt-[70px] flex h-[140px] w-28 items-center justify-center rounded-[14px] border border-line bg-paper-raised shadow-float"
              initial={still ? pose : { rotate: 0, x: 0, y: 12 }}
              animate={pose}
              transition={still ? { duration: 0 } : { ...SPRING, delay: i * 0.03 }}
            >
              {i === STACK.length - 1 && (
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-wash text-ink-2">
                  <ArrowDown className="h-5 w-5" />
                </span>
              )}
            </motion.div>
          ))}
        </div>
        <div className="flex flex-col items-center gap-1.5 text-center">
          <p className="font-display text-[22px] font-semibold leading-7 text-ink">{title}</p>
          <p className="text-[13px] text-ink-3">Images, videos, PDFs and documents</p>
        </div>
      </div>
    </motion.div>
  );
}

function Thumb({ preview, index }: { preview: UploadPreview; index: number }) {
  const Icon = preview.kind === 'video' ? Film : preview.kind === 'pdf' ? FileText : FileIcon;
  return (
    <span
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-line bg-wash text-ink-3 ring-2 ring-paper-raised',
        index > 0 && '-ml-6',
      )}
      style={{ zIndex: 3 - index }}
    >
      {preview.url ? <img src={preview.url} alt="" className="h-full w-full object-cover" /> : <Icon className="h-4 w-4" aria-hidden />}
    </span>
  );
}

export interface UploadTrayState {
  total: number;
  completed: number;
  current: string;
  /** 0 to 100 for the file being uploaded */
  progress: number;
  stage: 'compressing' | 'uploading' | 'analyzing' | 'complete';
  stageText?: string;
  previews: UploadPreview[];
}

/** In the corner while files upload and just after: what is coming in, what is happening, how far along. */
export function UploadTray({ state, done, mode, inset = 0 }: { state: UploadTrayState; done: boolean; mode: 'canvas' | 'wall'; inset?: number }) {
  const still = useReducedMotion();
  const reading = !done && state.stage === 'analyzing';
  const percent = done ? 100 : Math.min(100, ((state.completed + state.progress / 100) / state.total) * 100);
  const title = done
    ? mode === 'canvas' ? 'Added to your board' : 'Saved to Mindfull'
    : reading
      ? 'Reading with Partnr'
      : `Adding ${plural(state.total, 'item')}`;
  const detail = done
    ? plural(state.total, 'item')
    : state.stageText && state.stage !== 'uploading'
      ? state.stageText
      : state.total > 1
        ? `${state.current} · ${Math.min(state.completed + 1, state.total)} of ${state.total}`
        : state.current;

  return (
    <motion.div
      role="status"
      aria-live="polite"
      // Above the canvas toolbar, which runs along the bottom
      className="fixed bottom-[92px] z-[200] w-[300px] rounded-[18px] border border-line bg-paper-raised p-3 shadow-float"
      style={{ right: 24 + inset }}
      initial={still ? { opacity: 0 } : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, transition: { duration: still ? 0 : 0.18, ease: EASE_OUT } }}
      transition={still ? { duration: 0 } : SPRING}
      data-testid="upload-tray"
    >
      <div className="flex items-center gap-3">
        <div className="flex shrink-0 items-center pl-0.5">
          {state.previews.slice(0, 3).map((p, i) => (
            <Thumb key={`${p.name}-${i}`} preview={p} index={i} />
          ))}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13px] font-semibold text-ink">{title}</span>
          <span className="truncate text-[12px] text-ink-3">{detail}</span>
        </div>
        {done && (
          <motion.span
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-paper-raised"
            initial={still ? { opacity: 0 } : { opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={still ? { duration: 0 } : SPRING}
          >
            <Check className="h-3.5 w-3.5" aria-hidden />
          </motion.span>
        )}
      </div>
      <div className="mt-3 h-[2px] overflow-hidden rounded-full bg-line">
        <div
          className={cn('h-full rounded-full transition-[width,background-color] duration-300 ease-out motion-reduce:transition-none', reading ? 'bg-signal' : 'bg-ink')}
          style={{ width: `${percent}%` }}
        />
      </div>
    </motion.div>
  );
}
